/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect, useRef } from "react";

/*============================================================
  MATERIAL UI
============================================================*/
import { Button } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

/*============================================================
  PROPRIETARY - COMPONENTS
============================================================*/
import GridDropdown from "../../../widgets/GridDropdown";
import GridTextfield from "../../../widgets/GridTextfield";

import Multiplier from "./Multiplier.js";

/*============================================================
  PROPRIETARY - UTILS
============================================================*/
import { reNumbers } from "../../../../utils/fieldValidator.js";
import { validateCurrencyField3 } from "../../../../utils/fieldValidator.js";
import { TranslateRounded } from "@mui/icons-material";

var riderMap = new Map();

var prodRider = {}; //Single Rider Object

export default function Rider({ companyId, productRecommendation, childRef }) {
  //REFERENCES TO CHILD COMPONENT
  const multiplierRef = useRef(null);

  // const [riderOnly, setRiderOnly] = useState("");
  const [riderId, setRiderId] = useState("");
  const [riderName, setRiderName] = useState("");
  const [riderSumAssured, setRiderSumAssured] = useState("");
  const [riderPremium, setRiderPremium] = useState("");
  const [riderCoverageTerm, setRiderCoverageTerm] = useState("");
  const [riderCoverageTermError, setRiderCoverageTermError] = useState("");
  const [riderPremiumTerm, setRiderPremiumTerm] = useState("");
  const [riderPremiumTermError, setRiderPremiumTermError] = useState("");

  //EFFECT STATE
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  var [riderData, setRiderData] = useState([]);

  //SELECTED RIDER TABLE ROW
  const [selectedRow, setSelectedRow] = useState(0);
  const [prodRiderList, setProdRiderList] = useState([]);

  /**
   * REFERENCE USED BY PARENT COMPONENT TO CALL THIS CHILD'S FUNCTION
   * function reference Name : actual function name
   */
  childRef.current = {
    clearScreen: clearScreen,
    getRiderList: getRiderList,
    clearRiderList: clearRiderList,
  };

  /**==============================
   * EFFECTS - RIDER
   ==============================*/
  useEffect(() => {
    const fetchRider = async () => {
      riderData = [];
      riderMap.clear();

      try {
        const response = await fetch(`https://fp-api.3nity.tech/insurance_rider/${companyId}`);
        if (!response.ok) throw new Error("Failed to fetch rider list");
        const data = await response.json();
        const riderList = data.insurancerider_list;
        riderList.map((rider) => {
          riderMap.set(rider.id, rider.name);
          riderData.push({ key: rider.id, value: rider.name });
        });

        setRiderData(riderData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRider();
  }, [companyId]);

  /**==============================
     * ACTION HANDLER
     ==============================*/
  function handleChange(e, object) {
    let id = "";

    let actionType = "";
    let updateAction = null;
    let payload = null;

    if (e instanceof PointerEvent) {
      id = object.props.id;

      if (id === "riderName") {
        productRecommendation.riderId = e.target.value;
        productRecommendation.riderName = riderMap.get(e.target.value);

        prodRider.riderName = riderMap.get(e.target.value);
        setRiderName(e.target.value);
        //actionType = "UPDATE_RIDER";
      }
    } else {
      id = e.target.id;

      let input_data = {
        event: e,
        fieldName: "",
        fieldVariable: "",
        isMandatory: false,
      };

      // if (id === "riderOnly") {
      //   productRecommendation.riderOnly = e.target.checked;
      //   setRiderOnly(e.target.checked);
      //   //actionType = "UPDATE_RIDER_ONLY";
      // }

      if (id === "riderSumAssured") {
        input_data.fieldName = "Sum assured";
        input_data.fieldVariable = riderSumAssured;
        const result = validateCurrencyField3(input_data);
        if (result.errorMessage === "") {
          setRiderSumAssured(result.value);
          prodRider.riderSumAssured = result.value;
          //actionType = "UPDATE_RIDER_SUM_ASSURED";
        }
      }

      if (id === "riderPremium") {
        input_data.fieldName = "Rider premium";
        input_data.fieldVariable = riderPremium;
        const result = validateCurrencyField3(input_data);
        if (result.errorMessage === "") {
          setRiderPremium(result.value);

          prodRider.riderPremium = result.value;
          //actionType = "UPDATE_RIDER_PREMIUM";
        }
      }

      if (id === "riderCoverageTerm" && reNumbers.test(e.target.value)) {
        if (e.target.value.length === 0) {
          setRiderCoverageTermError("Coverage term is required.");
        } else if (e.target.value.length > 3) {
          setRiderCoverageTermError("Coverage  term exceeded 3 characters");
        } else setRiderCoverageTermError("");

        setRiderCoverageTerm(e.target.value);
        prodRider.riderCoverageTerm = e.target.value;
        //actionType = "UPDATE_RIDER_COVERAGE_TERM";
      }

      if (id === "riderPremiumTerm" && reNumbers.test(e.target.value)) {
        if (e.target.value.length === 0) {
          setRiderPremiumTermError("Premium term is required.");
        } else if (e.target.value.length > 3) {
          setRiderPremiumTermError("Premium term exceeded 3 characters");
        } else setRiderPremiumTermError("");

        setRiderPremiumTerm(e.target.value);
        prodRider.riderPremiumTerm = e.target.value;
        //actionType = "UPDATE_RIDER_PREMIUM_TERM";
      }
    }
  }

  /**==================================================
   * VALIDATION - ENSURE MANDATORY FIELDS ARE POPULATED
   ==================================================*/
  function validate() {
    if (riderName.length <= 0) return false;
    if (riderSumAssured.length <= 0) return false;
    if (riderPremium.length <= 0) return false;
    if (riderCoverageTerm.length <= 0) return false;
    if (riderPremiumTerm.length <= 0) return false;

    //VALIDATE PRODUCT MULTIPLIER
    if (multiplierRef.current) {
      if (multiplierRef.current.validate() === false) return false;
    }

    return true;
  }

  /**==================================================
   * ADD RIDER INTO RIDER LIST
   ==================================================*/
  function handleAddRider() {
    //WHEN VALIDATION FAILS - DON'T PROCEED
    if (!validate()) return;

    const newRows = [...prodRiderList, prodRider];
    setProdRiderList(newRows);
    productRecommendation.prodRiderList = prodRiderList;

    console.log("riderName = " + riderName);
    console.log(prodRiderList.length);
    console.log(prodRiderList);

    prodRider = {};

    //Clear UI components
    clearScreen();
  }

  /**==================================================
   * SELECTED ROW - FUTURE ENHANCEMENT TO HANDLE EDIT
   ==================================================*/
  function handleSelectRow() {
    //console.log("handleSelectRow");
  }

  /**==================================================
   * REMOVE A TARGETED RIDER FROM RIDER LIST
   ==================================================*/
  function removeRider(index) {
    var tempRiderList = prodRiderList.slice();
    tempRiderList.splice(index, 1);
    setProdRiderList(tempRiderList);
  }

  //Reset Rider Components
  function clearScreen() {
    // setRiderOnly("");
    setRiderId("");
    setRiderName("");
    setRiderSumAssured("");
    setRiderPremium("");
    setRiderCoverageTerm("");
    setRiderPremiumTerm("");

    if (multiplierRef.current) multiplierRef.current.clearScreen();
  }

  function getRiderList() {
    return prodRiderList;
  }

  function clearRiderList() {
    return setProdRiderList([]);
  }

  return (
    <>
      <Grid size={12}>
        <Typography variant="h6" gutterBottom sx={{ backgroundColor: "lightgray" }}>
          Rider Details
        </Typography>
      </Grid>

      {/* IS RIDER ONLY  */}
      {/* <GridCheckbox
        id="riderOnly"
        columnSize="12"
        label="Add rider only."
        value="riderOnly"
        disabled={false}
        onChange={handleChange}
        checked={riderOnly}
      /> */}

      {/* RIDER NAME */}
      <GridDropdown
        id="riderName"
        key="riderName"
        columnSize="12"
        label="Rider Name"
        currState={riderName}
        onChange={handleChange}
        data={riderData}
      />

      {/* POLICY TERM */}
      <GridTextfield
        id="riderSumAssured"
        key="riderSumAssured"
        columnSize="6"
        label="Sum Assured"
        currState={riderSumAssured}
        onChange={handleChange}
      />

      {/* RIDER COVERAGE TERM */}
      <GridTextfield
        id="riderCoverageTerm"
        key="riderCoverageTerm"
        columnSize="6"
        label="Rider Coverage Term"
        currState={riderCoverageTerm}
        onChange={handleChange}
        error={riderCoverageTermError}
        helperText={riderCoverageTermError}
      />

      {/* RIDER PREMIUM TERM */}
      <GridTextfield
        id="riderPremium"
        key="riderPremium"
        columnSize="6"
        label="Premium"
        currState={riderPremium}
        onChange={handleChange}
      />

      {/* RIDER PREMIUM TERM */}
      <GridTextfield
        id="riderPremiumTerm"
        key="riderPremiumTerm"
        columnSize="6"
        label="Premium Term"
        currState={riderPremiumTerm}
        onChange={handleChange}
        error={riderPremiumTermError}
        helperText={riderPremiumTermError}
      />

      {/*==============================
         MULTIPLIER / ADDITIONAL COVER DETAILS
        ==============================*/}
      <Multiplier title="Rider Multiplier" productRecommendation={prodRider} childRef={multiplierRef} />

      {/*==============================
         RIDER LIST
        ==============================*/}
      <Grid size={12}>
        {prodRiderList.length >= 1 && (
          <Accordion>
            <AccordionSummary expandIcon={<ArrowDropDownIcon />} aria-controls="panel1-content" id="panel1-header">
              <Typography variant="body2" align="justify" sx={{ textDecoration: "underline", fontWeight: 1000 }}>
                Rider List
              </Typography>
            </AccordionSummary>

            {prodRiderList.map((rider, index) => (
              <>
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ArrowDropDownIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <Typography variant="body2" align="justify" sx={{ fontWeight: 1000 }}>
                      RIDER {index + 1}
                    </Typography>
                  </AccordionSummary>
                  <TableContainer component={Paper}>
                    <TableHead>
                      <TableRow>
                        <TableCell align="left">Rider Name</TableCell>
                        <TableCell align="left">Sum Assured</TableCell>
                        <TableCell align="left">Premium</TableCell>
                        <TableCell align="left">Premium Term</TableCell>
                        <TableCell align="left">Policy Term</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <TableRow
                        key={rider.index}
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                        selected={selectedRow === index + 1}
                        onClick={() => handleSelectRow(index + 1, rider)}
                      >
                        <TableCell align="left">{rider.riderName}</TableCell>
                        <TableCell align="left">{rider.riderSumAssured}</TableCell>
                        <TableCell align="left">{rider.riderPremium}</TableCell>
                        <TableCell align="left">{rider.riderPremiumTerm}</TableCell>
                        <TableCell align="left">{rider.riderCoverageTerm}</TableCell>
                        <TableCell align="left">
                          <Button variant="text" onClick={() => removeRider(index)} sx={{ color: "red" }}>
                            X
                          </Button>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </TableContainer>

                  {rider.addProductMultiplier && (
                    <>
                      <Accordion>
                        <AccordionSummary
                          expandIcon={<ArrowDropDownIcon />}
                          aria-controls="panel1-content"
                          id="panel1-header"
                        >
                          <Typography variant="body2" align="justify" sx={{ fontWeight: 1000 }}>
                            RIDER {index + 1} - MULTIPLER
                          </Typography>
                        </AccordionSummary>

                        <TableContainer component={Paper}>
                          <TableHead>
                            <TableRow>
                              <TableCell align="left">Additional Cover</TableCell>
                              <TableCell align="left">Premium</TableCell>
                              <TableCell align="left">Premium Term</TableCell>
                              <TableCell align="left">Policy Term</TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            <TableCell align="left">{rider.prodMultiplierSumAssured}</TableCell>
                            <TableCell align="left">{rider.prodMultiplierPremium}</TableCell>
                            <TableCell align="left">{rider.prodMultiplierPremiumTerm}</TableCell>
                            <TableCell align="left">{rider.prodMultiplierPolicyTerm}</TableCell>
                          </TableBody>
                        </TableContainer>
                      </Accordion>
                    </>
                  )}
                </Accordion>
              </>
            ))}
          </Accordion>
        )}
      </Grid>

      <Grid size={12}>
        <Stack spacing={2} direction="row" justifyContent="center">
          <Button variant="contained" sx={{ width: "140px" }} disabled={false} onClick={handleAddRider}>
            Add Rider
          </Button>
        </Stack>
      </Grid>
    </>
  );
}
